import { useStorage } from "@platform/hooks";
import { SortModelItem } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { useAgGridOptions } from "@platform/design-system";

import { RFP_STATUSES, RfpPageState } from "../constants/rfp";
import { RFPS_DRAFT, RFPS_SUBMITTED } from "../constants";
import { DraftRfp, Rfp } from "../types/clientTab";
import { draftHeaders, headers } from "../pages/RFPs/utils";
import { handleSort } from "../utils";

import { useClient } from "./useClient";
import {
  UseRfpsPageDataOptions,
  UseRfpsPageDataReturn,
} from "./useMainRfpsPageDataV2";
import { useRfpsTab } from "./useRfpsTab";
import { useDiscardRfpDraft } from "./useDiscardRfpDraft";

export const useClientRfpsPageDataV2 = ({
  clientId,
  setError,
}: UseRfpsPageDataOptions): UseRfpsPageDataReturn => {
  const { getStorageItem, setStorageItem } = useStorage("session");

  // Session storage & state initialization
  const sessionStorageKey = `clientRfpPageState-${clientId}`;
  const stored = getStorageItem(sessionStorageKey);
  let rfpPageState: RfpPageState = {};
  if (stored && typeof stored === "object") {
    rfpPageState = stored as RfpPageState;
  }
  // Validate that currStatus contains only valid camelCase keys from RFP_STATUSES
  // This prevents issues if old display values were stored
  const validKeys = Object.keys(RFP_STATUSES);
  const sessionCurrStatus: string[] = (rfpPageState.currStatus || []).filter(
    (status) => validKeys.includes(status),
  );

  const savedSort = rfpPageState.sortModel?.[0];

  const [currStatus, setCurrStatus] = useState<string[]>(
    sessionCurrStatus || [],
  );
  const [sortBy, setSortBy] = useState<string>(savedSort?.colId);
  const [isDescending, setIsDescending] = useState<boolean>(
    savedSort?.sort === "desc" || false,
  );
  const [debouncedQuery, setDebouncedQuery] = useState<string>(
    rfpPageState.query || "",
  );

  const debouncedSearch = useRef(
    debounce((value: string) => {
      const trimmed = value.trim();
      const lower = trimmed.toLowerCase();
      if (trimmed.length >= 3 || trimmed.length === 0) setDebouncedQuery(lower);
    }, 300),
  ).current;

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  // Discard draft mutation using shared hook
  const { handleDiscardRfpDraft, isPending: isDiscardPending } =
    useDiscardRfpDraft({
      queryStrategy: "client",
      clientId: clientId?.toString(),
      setError,
    });

  const { handleTabChange, tabIndex, initialTabIndex } = useRfpsTab({
    setDebouncedQuery,
    setCurrStatus,
    setSortBy,
    setIsDescending,
    rfpPageState,
    sessionStorageKey,
  });

  // Fetch client data which includes rfpList and draftRfpList
  const clientQuery = useClient({ clientId: clientId.toString() });

  useEffect(() => {
    setError(clientQuery?.error?.message);
  }, [clientQuery?.error, setError]);

  // Get data from client query - use the actual types from the client endpoint
  const clientData = clientQuery.data;

  const submittedRfps: Rfp[] = useMemo(
    () => clientData?.rfpList || [],
    [clientData?.rfpList],
  );
  const draftRfps: DraftRfp[] = useMemo(
    () => clientData?.draftRfpList || [],
    [clientData?.draftRfpList],
  );

  const rows: (DraftRfp | Rfp)[] = useMemo(() => {
    if (!sortBy) return [];
    let data = tabIndex === RFPS_SUBMITTED ? submittedRfps : draftRfps;
    // Apply status filter to submitted RFPs only (drafts don't have status filters)
    if (tabIndex === RFPS_SUBMITTED && currStatus.length > 0) {
      data = data.filter((rfp) => {
        // Cast to Rfp type to access details.status property
        const submittedRfp = rfp as Rfp;
        return currStatus.includes(submittedRfp.details.status);
      });
    }

    // Apply search filter if query exists
    if (debouncedQuery) {
      data = data.filter((rfp) => {
        const searchableText = JSON.stringify(rfp).toLowerCase();
        return searchableText.includes(debouncedQuery);
      });
    }

    // Apply sorting
    const currentHeaders = tabIndex === RFPS_SUBMITTED ? headers : draftHeaders;
    const headerConfig = currentHeaders.find((h) => h.field === sortBy);

    const order = isDescending ? "desc" : "asc";
    const sortedData = handleSort(
      [...data], // Create a new array to ensure React detects the change
      order,
      sortBy,
      headerConfig?.cellDataType as string,
      headerConfig?.context?.onNull,
    ) as (Rfp | DraftRfp)[];

    return sortedData;
  }, [
    tabIndex,
    submittedRfps,
    draftRfps,
    debouncedQuery,
    currStatus,
    sortBy,
    isDescending,
  ]);

  const isDataFetching = clientQuery.isFetching;

  // Cast filtered data to RfpListItem[] for component compatibility
  const onSortPersistState = useCallback((sortModel: SortModelItem) => {
    const { sort, colId } = sortModel;

    setSortBy(colId);
    setIsDescending(sort === "desc" || false);
  }, []);

  useEffect(() => {
    const currentState: RfpPageState = (getStorageItem(sessionStorageKey) ||
      {}) as RfpPageState;
    setStorageItem(sessionStorageKey, {
      ...currentState,
      currStatus,
      tabIndex,
      query: debouncedQuery,
    });
  }, [
    currStatus,
    debouncedQuery,
    getStorageItem,
    sessionStorageKey,
    setStorageItem,
    tabIndex,
  ]);

  const initialColId = useMemo(
    () =>
      savedSort?.colId ??
      (initialTabIndex === RFPS_DRAFT ? "lastModifyDate" : "client.name"),
    [initialTabIndex, savedSort?.colId],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { resetPage, ...dataTableOptions } = useAgGridOptions({
    sessionStorageKey,
    sort: savedSort?.sort ?? (initialTabIndex === RFPS_DRAFT ? "desc" : "asc"),
    colId: initialColId,
    onSortPersistState,
  });

  return {
    tabIndex,
    handleTabChange,
    submittedCount: submittedRfps.length,
    draftCount: draftRfps.length,
    debouncedSearch,
    debouncedQuery,
    currStatus,
    setCurrStatus,
    dataTableOptions: {
      ...dataTableOptions,
      rowData: rows,
      loading: isDataFetching || isDiscardPending,
    },
    handleDiscardRfpDraft,
  };
};
